import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './providers/leaflet/Leaflet.css';
import { mapConfig } from './utils/mapConfig';
import { createCustomIcon, createUserLocationIcon, leafletTileConfig } from './providers/leaflet/leafletConfig';
import { IMapProvider, MapProviderProps, MapPlace } from './IMapProvider';

// Fix for default marker icons in React-Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

// Component to handle map click events
const MapClickHandler: React.FC<{
  onClick?: (lat: number, lng: number) => void;
  onRightClick?: (lat: number, lng: number) => void;
}> = ({ onClick, onRightClick }) => {
  useMapEvents({
    click: (e) => {
      if (onClick) {
        onClick(e.latlng.lat, e.latlng.lng);
      }
    },
    contextmenu: (e) => {
      if (onRightClick) {
        onRightClick(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
};

// Leaflet implementation component
const LeafletMap: React.FC<MapProviderProps> = ({
  center,
  zoom = mapConfig.defaultZoom,
  places,
  userLocation,
  searchRadius,
  onRadiusChange,
  locationAddress,
  onMapClick,
  onMapRightClick,
  onPlaceClick,
  getPlaceDetailUrl,
  customMarkers = [],
  showRadiusCircle = true,
}) => {
  const mapRef = useRef<L.Map | null>(null);

  // Update map center when user location changes (only pan, don't change zoom)
  useEffect(() => {
    if (mapRef.current && userLocation) {
      mapRef.current.panTo(userLocation);
    }
  }, [userLocation?.[0], userLocation?.[1]]);

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      style={{ height: '100%', width: '100%' }}
      ref={mapRef}
    >
      <TileLayer
        attribution={leafletTileConfig.attribution}
        url={leafletTileConfig.tileLayerUrl}
        maxZoom={mapConfig.maxZoom}
        minZoom={mapConfig.minZoom}
      />

      {/* Map click handler */}
      <MapClickHandler onClick={onMapClick} onRightClick={onMapRightClick} />

      {/* User location marker */}
      {userLocation && (
        <>
          <Marker position={userLocation} icon={createUserLocationIcon() as L.Icon}>
            <Popup>
              <div className="map-popup">
                <h3>Your Location</h3>
                <p>Lat: {userLocation[0].toFixed(6)}</p>
                <p>Lng: {userLocation[1].toFixed(6)}</p>
              </div>
            </Popup>
          </Marker>

          {/* Radius circle */}
          {showRadiusCircle && (
            <Circle
              center={userLocation}
              radius={searchRadius}
              pathOptions={{
                color: '#ff7297',
                fillColor: '#ff7297',
                fillOpacity: 0.1,
                weight: 2,
              }}
            />
          )}
        </>
      )}

      {/* Places markers */}
      {places.map((place) => (
        <Marker
          key={place.id}
          position={[place.lat, place.lng]}
          icon={createCustomIcon(place.sportType)}
          eventHandlers={{
            click: () => onPlaceClick && onPlaceClick(place),
          }}
        >
          <Popup maxWidth={300}>
            <div className="map-popup place-popup">
              <h3>{place.name}</h3>
              <p className="popup-address">{place.address}</p>
              <p className="popup-description">{place.description}</p>

              <div className="popup-details">
                <span className="popup-tag sport-tag">{place.sportType}</span>
                <span className="popup-tag type-tag">{place.placeType}</span>
              </div>

              {place.rating && (
                <div className="popup-rating">
                  Rating: {place.rating} / 5
                </div>
              )}

              <div className="popup-badges">
                {place.isOpen && <span className="badge open-badge">Open Now</span>}
                {place.isFree && <span className="badge free-badge">Free Entry</span>}
                {place.isAccessible && <span className="badge accessible-badge">Accessible</span>}
              </div>

              <div className="popup-actions">
                {getPlaceDetailUrl ? (
                  <a className="popup-btn primary" href={getPlaceDetailUrl(place)}>
                    View Details
                  </a>
                ) : (
                  <button className="popup-btn primary" disabled>View Details</button>
                )}
                <button className="popup-btn secondary">Get Directions</button>
              </div>
            </div>
          </Popup>
        </Marker>
      ))}

      {/* Custom markers added by user */}
      {customMarkers.map((marker) => (
        <Marker
          key={marker.id}
          position={[marker.lat, marker.lng]}
        >
          <Popup>
            <div className="map-popup">
              <h3>{marker.name}</h3>
              <p>Custom Location</p>
              <p>Lat: {marker.lat.toFixed(6)}</p>
              <p>Lng: {marker.lng.toFixed(6)}</p>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

/**
 * Leaflet implementation of the IMapProvider interface
 */
export class LeafletMapProvider implements IMapProvider {
  renderMap(props: MapProviderProps): JSX.Element {
    return <LeafletMap {...props} />;
  }
}